/**
 * Macro-aware rich text field with inline token pills and typeahead.
 *
 * Wraps the generic RichTextEditor from @chatsdet/ui and wires it to
 * the macro catalog + workflow variables for `$` typeahead.
 *
 * Drop-in replacement for MacroTextField and MacroTextArea.
 */

import React, { useMemo } from 'react';
import {
  RichTextEditor,
  TOKEN_COLORS,
  type RichTextEditorProps,
  type RichTextEditorRef,
  type TokenPattern,
} from '@chatsdet/ui';
import {
  MACRO_PATTERN,
  VARIABLE_PATTERN,
  slugifyStepLabel,
  validateMacroReferences,
} from '@chatsdet/types';
import type { VariableTypeaheadEntry } from '@chatsdet/types';
import { useWorkflowVariablesContext } from '../../pages/FlowDesigner/context/WorkflowVariablesContext';
import { createMacroSuggestionTrigger } from './macroTypeahead';

const MACRO_TOKEN_PATTERNS: TokenPattern[] = [
  { regex: MACRO_PATTERN, type: 'macro' },
  { regex: VARIABLE_PATTERN, type: 'variable' },
];
const MACRO_ONLY_TOKEN_PATTERNS: TokenPattern[] = [{ regex: MACRO_PATTERN, type: 'macro' }];

const TOKEN_COLOR_MAP = {
  variable: TOKEN_COLORS.variable,
  macro: TOKEN_COLORS.macro,
};

interface MacroRichTextFieldProps extends Omit<
  RichTextEditorProps,
  'tokenPatterns' | 'tokenColors' | 'triggers'
> {
  /** Accepted for backward compat but ignored (rich editor has no HTML input type) */
  type?: string;
  /** For backward compat with MacroTextArea */
  formatJson?: boolean;
  /** Include `$var.*` entries alongside macros in typeahead. Defaults to true. */
  includeVariables?: boolean;
  /** Override context-derived variable suggestions for order-sensitive editors. */
  suggestionVariables?: readonly VariableTypeaheadEntry[];
}

export const MacroRichTextField = React.forwardRef<RichTextEditorRef, MacroRichTextFieldProps>(
  (
    {
      value,
      onChange,
      errorMessage: externalError,
      type: _type,
      formatJson: _formatJson,
      includeVariables = true,
      suggestionVariables,
      ...props
    },
    ref
  ) => {
    const workflowVariables = useWorkflowVariablesContext();

    const variableEntries: VariableTypeaheadEntry[] = useMemo(() => {
      const entries: VariableTypeaheadEntry[] = [];
      for (const v of workflowVariables) {
        if (v.hasCollision) {
          for (const definer of v.allDefiners) {
            const qualifier = slugifyStepLabel(definer.stepIndex, definer.stepLabel);
            entries.push({
              kind: 'variable' as const,
              name: v.name,
              source: v.source,
              description: `From Step ${definer.stepIndex}: ${definer.stepLabel}`,
              qualifiedName: `${qualifier}.${v.name}`,
              stepLabel: `Step ${definer.stepIndex}: ${definer.stepLabel}`,
              designTimeValue: v.designTimeValue ?? undefined,
            });
          }
        } else {
          const stepDesc =
            v.definedByStepIndex && v.definedByStepLabel
              ? `Step ${v.definedByStepIndex}: ${v.definedByStepLabel}`
              : null;
          const description =
            v.description ??
            stepDesc ??
            (v.hasDesignTimeValue ? `Variable from ${v.source}` : 'Populated at runtime');
          entries.push({
            kind: 'variable' as const,
            name: v.name,
            source: v.source,
            description,
            designTimeValue: v.designTimeValue ?? undefined,
          });
        }
      }
      return entries;
    }, [workflowVariables]);

    const availableVariableEntries = suggestionVariables ?? variableEntries;
    const triggers = useMemo(
      () => [createMacroSuggestionTrigger(availableVariableEntries, includeVariables)],
      [availableVariableEntries, includeVariables]
    );

    const macroIssues = useMemo(() => validateMacroReferences(value), [value]);
    const macroError = macroIssues[0]?.message.replaceAll('`', '');
    const errorMessage = externalError || macroError;

    return (
      <RichTextEditor
        ref={ref}
        {...props}
        value={value}
        onChange={onChange}
        tokenPatterns={includeVariables ? MACRO_TOKEN_PATTERNS : MACRO_ONLY_TOKEN_PATTERNS}
        tokenColors={TOKEN_COLOR_MAP}
        triggers={triggers}
        errorMessage={errorMessage}
      />
    );
  }
);

MacroRichTextField.displayName = 'MacroRichTextField';
